import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Shell from '../Landing/Chrome';
import Recaptcha from '../Recapcha/recapcha';
import QuestionField from './QuestionField';
import { proveMembership, fetchGroup } from '../../lib/anonIdentity';

const FormResponsePreview = () => {
  const [formData, setFormData] = useState(null);
  const [allResponses, setAllResponses] = useState({});
  const [responseId, setResponseId] = useState(null);
  const [decryptedResponse, setDecryptedResponse] = useState(null);
  const [unlockAt, setUnlockAt] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, submitted, decrypted
  const [formStatus, setFormStatus] = useState('loading'); // loading, active, closed, draft, error
  const [timeRemaining, setTimeRemaining] = useState(null); // Countdown for decryption
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [proving, setProving] = useState(false);
  const [groupSize, setGroupSize] = useState(null);
  const userId = JSON.parse(localStorage.getItem('uid'));

  const { id } = useParams();
  const formId = id;

  const clearForm = () => {
    setAllResponses({});
    setErrors({});
    setIsCaptchaVerified(false);
    const form = document.getElementById('responseForm');
    if (form) form.reset();
  };

  useEffect(() => {
    fetchGroup().then((g) => setGroupSize(g.size)).catch(() => {});
  }, []);

  // Fetch form data and status
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_LOCAL_URL}/api/forms/${formId}`);
        const data = await response.json();

        if (data.status === 'draft') setFormStatus('draft');
        else if (data.status === 'active') setFormStatus('active');
        else if (data.status === 'closed') setFormStatus('closed');
        else setFormStatus('error');

        setFormData(data);
        if (data.encryptionEndTime) {
          const remainingTime = new Date(data.encryptionEndTime) - new Date();
          setTimeRemaining(remainingTime > 0 ? remainingTime : 0);
        }
      } catch (error) {
        console.error('Error fetching form:', error);
        toast.error('Error loading form data');
        setFormStatus('error');
      }
    };
    fetchForm();
  }, [formId]);

  useEffect(() => {
    if (responseId) {
      setStatus('submitted');
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_LOCAL_URL}/api/responses/${responseId}`);
          if (res.ok) {
            const data = await res.json();
            setDecryptedResponse(data.decryptedAnswers);
            setStatus('decrypted');
            clearInterval(interval);
          } else if (res.status === 403) {
            const { unlockAt: unlockTime } = await res.json();
            setUnlockAt(new Date(unlockTime));
          }
        } catch (error) {
          console.error('Error fetching decrypted response:', error);
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [responseId]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => setTimeRemaining((prev) => prev - 1000), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining]);

  const handleResponseChange = (questionId, value) => {
    setAllResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCaptchaChange = (value) => setIsCaptchaVerified(!!value);

  const handleSubmit = async () => {
    try {
      if (!userId) {
        toast.info('Please sign in (top right) to submit your response.');
        return;
      }
      if (formData?.user === userId) {
        toast.info('You can’t answer your own survey.');
        return;
      }
      if (formStatus !== 'active') {
        toast.error('This survey is not active. Submissions are not allowed.');
        return;
      }
      if (!isCaptchaVerified) {
        toast.error('Please complete the verification first.');
        return;
      }

      const newErrors = {};
      const requiredQuestions = formData.questions.filter((q) => q.required);
      const unansweredRequired = requiredQuestions.filter((q) => !allResponses[q._id]);
      if (unansweredRequired.length > 0) {
        unansweredRequired.forEach((q) => (newErrors[q._id] = true));
        setErrors(newErrors);
        toast.error('Please answer all required questions before submitting.');
        return;
      }

      // The answers are signed with a zero-knowledge proof made in this browser.
      // Only the proof and the answers are sent. No user id leaves this device.
      const payload = JSON.stringify(allResponses);
      setProving(true);
      let proof;
      try {
        proof = await proveMembership(userId, formId, payload);
      } catch (e) {
        setProving(false);
        if (e.code === 'NO_IDENTITY') toast.error('Your anonymous identity isn’t on this device. Sign out and back in, or restore it from your profile.');
        else if (e.code === 'NOT_IN_GROUP') toast.error('Your identity isn’t registered yet. Please sign in again.');
        else toast.error('Could not create your private proof. Please try again.');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_LOCAL_URL}/api/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formId, payload, proof }),
      });
      setProving(false);
      const data = await response.json();

      if (!response.ok) {
        switch (data.code) {
          case 'DUPLICATE_SUBMISSION': toast.warning('You’ve already submitted this survey.'); break;
          case 'FORM_NOT_FOUND': toast.error('This survey no longer exists.'); break;
          case 'MISSING_FIELDS': toast.error('Please fill in all required fields.'); break;
          case 'UNKNOWN_GROUP': toast.error('The anonymous group changed. Please try submitting again.'); break;
          case 'INVALID_PROOF': toast.error('Your private proof was rejected. Please try again.'); break;
          default: toast.error('An error occurred while submitting.');
        }
        throw new Error(data.message);
      }

      toast.success('Response submitted.');
      setResponseId(data.data.responseId);
      setUnlockAt(new Date(data.data.unlockAt));
      clearForm();
    } catch (error) {
      setProving(false);
      console.error('Error submitting response:', error);
    }
  };

  if (formStatus === 'loading') return <Shell><div className="zt-center">Loading survey…</div></Shell>;
  if (formStatus === 'error' || !formData) return <Shell><div className="zt-center">We couldn’t load this survey.</div></Shell>;

  const statusTag = { active: 'Open', draft: 'Draft', closed: 'Closed' }[formStatus];
  const hh = timeRemaining > 0 ? new Date(timeRemaining).toISOString().substr(11, 8) : null;

  return (
    <Shell>
      <div className="zt-page">
        <div className="zt-narrow">
          <div className="zt-actions" style={{ marginBottom: 6 }}>
            <span className="zt-tag solid">{statusTag}</span>
            {formData.category && <span className="zt-tag">{formData.category}</span>}
          </div>
          <h1 className="zt-page-title">{formData.title}</h1>
          <p className="zt-page-sub">{formData.description}</p>
          <div className="zt-note">
            <b>Your answers are anonymous.</b> Your browser proves you’re a registered member without saying which one, so the server can’t tell who wrote what. {groupSize !== null && <span>The anonymous group currently has <b>{groupSize}</b> member{groupSize === 1 ? '' : 's'}{groupSize < 20 ? ', so protection grows as more people join' : ''}.</span>}
          </div>

          {formStatus === 'draft' && <p className="zt-hint red">This survey is a draft and can’t accept responses yet.</p>}
          {formStatus === 'closed' && (
            <div className="zt-panel" style={{ marginTop: 28 }}>
              <span className="zt-tag solid">Closed</span>
              <h2 className="zt-page-title" style={{ fontSize: 36, marginTop: 14 }}>This survey has ended.</h2>
              <p className="zt-page-sub">See what everyone said. The results are open to all.</p>
              <div className="zt-actions" style={{ marginTop: 22 }}><a className="zt-btn primary" href={`/results/${formId}`}>View results →</a></div>
            </div>
          )}

          {formStatus === 'active' && status === 'idle' && (
            <>
              <form id="responseForm" onSubmit={(e) => e.preventDefault()}>
                {formData.questions.map((question, index) => (
                  <div key={`${question._id}-${index}`} className={`zt-panel zt-ask${errors[question._id] ? ' err' : ''}`} style={{ marginTop: 18 }}>
                    <div className="zt-qhead">
                      <span className="zt-mono">Q{String(index + 1).padStart(2, '0')}</span>
                      {question.required && <span className="zt-tag red">Required</span>}
                    </div>
                    <h3 className="zt-ask-t">{question.title}</h3>
                    <QuestionField question={question} value={allResponses[question._id]} onChange={(v) => handleResponseChange(question._id, v)} />
                    {errors[question._id] && <p className="zt-hint red">This question is required.</p>}
                  </div>
                ))}
              </form>

              <div className="zt-panel flat" style={{ marginTop: 26 }}>
                <label className="zt-label">Verify you’re human</label>
                <Recaptcha onChange={handleCaptchaChange} />
              </div>

              <div className="zt-actions" style={{ marginTop: 26, justifyContent: 'space-between' }}>
                <span className="zt-mono">{hh ? `Results unlock in ${hh}` : 'Results are time-locked'}</span>
                <button className="zt-btn primary" onClick={handleSubmit} disabled={proving}>{proving ? 'Creating your private proof…' : 'Submit anonymously →'}</button>
              </div>
            </>
          )}

          {status !== 'idle' && (
            <div className="zt-panel" style={{ marginTop: 28 }}>
              <span className="zt-tag red">Sealed</span>
              <h2 className="zt-page-title" style={{ fontSize: 38, marginTop: 14 }}>Thank you. Your answer is in.</h2>
              {status === 'submitted' && unlockAt && (
                <p className="zt-page-sub">It’s encrypted and will open on <b>{new Date(unlockAt).toLocaleString()}</b>. You can leave this page; nothing links it back to you.</p>
              )}
              {status === 'decrypted' && decryptedResponse && (
                <>
                  <p className="zt-page-sub">The time-lock has passed. Here’s what you submitted. <a href={`/results/${formId}`} style={{ textDecoration: 'underline' }}>See what everyone said →</a></p>
                  <div style={{ marginTop: 18 }}>
                    {decryptedResponse.map((item, index) => (
                      <div key={index} className="zt-answer">
                        <div className="zt-mono">{item.questionTitle}</div>
                        <p>{Array.isArray(item.answer) ? item.answer.join(', ') : item.answer}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
};

export default FormResponsePreview;
